import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useFormContext, useWatch } from "react-hook-form";

import { PictureKeys } from "../../constants/ratio";
import { FormTranslationsContext } from "../../contexts/FormTranslationsContext";
import usePictureTags from "../../hooks/usePictureTags";
import PictureRatio from "../pictures/PictureRatio";

import type { JSX } from "react";

import type {
  GetCastingResponse,
  GetMediaResponse,
} from "../../api/MediaAPI.types";
import type { GetOfferResponse } from "../../api/OfferAPI.types";
import type {
  GetDisplayResponse,
  GetSmartlistResponse,
  SmartlistSeason,
  SmartlistSeasonUpdated,
  UpdatedSmartlist,
} from "../../api/SmartlistAPI.types";
import type { TagType } from "../../hooks/usePictureTags";
import type { Picture } from "../../types";
import type { OfferFormState } from "../offers/offerAdapter";

export interface Translation {
  default: boolean;
  uuid: string;
  language_uuid: string;
  picture: Picture | null;
}

export interface ImageRatio {
  label: string;
  ratio: string;
  id: PictureKeys;
  subLabel?: string;
}

export const DEFAULT_IMAGE_RATIOS: ImageRatio[] = [
  {
    label: "16:6",
    id: PictureKeys.PICTURE_16_6,
    ratio: "16 / 6",
  },
  {
    label: "16:9",
    id: PictureKeys.PICTURE_16_9,
    ratio: "16 / 9",
  },
  {
    label: "3:4",
    id: PictureKeys.PICTURE_3_4,
    ratio: "3 / 4",
  },
  {
    label: "1:1",
    id: PictureKeys.PICTURE_1_1,
    ratio: "1 / 1",
  },
  {
    label: "2:3",
    id: PictureKeys.PICTURE_2_3,
    ratio: "2 / 3",
  },
  {
    label: "16:3",
    id: PictureKeys.PICTURE_16_3,
    ratio: "16 / 3",
  },
];

export interface UploadPictureState {
  isLoading: boolean;
  pictureFormat?: PictureKeys;
  translationId?: string;
}

export type ItemWithPicture =
  | GetMediaResponse
  | GetOfferResponse
  | GetSmartlistResponse
  | UpdatedSmartlist
  | SmartlistSeason
  | SmartlistSeasonUpdated
  | GetCastingResponse
  | GetDisplayResponse
  | OfferFormState;

interface FormPicturesProps<T extends ItemWithPicture> {
  isDisabled?: boolean;
  onUpload: ({
    file,
    translationId,
    pictureFormat,
  }: {
    file: File;
    translationId: string;
    pictureFormat: PictureKeys;
  }) => void;
  uploadPictureState: UploadPictureState;
  imageRatios?: ImageRatio[];
  item: T;
  itemType: TagType;
}

type Loadings = Record<string, Partial<Record<PictureKeys, boolean>>>;

export default function FormPictures<T extends ItemWithPicture>({
  isDisabled,
  onUpload,
  uploadPictureState,
  imageRatios = DEFAULT_IMAGE_RATIOS,
  item,
  itemType,
}: FormPicturesProps<T>): JSX.Element | null {
  const { activeLanguageIndex } = useContext(FormTranslationsContext);
  const { control, getValues, setValue } = useFormContext();

  const handleItemUpdate = useCallback(
    (updatedItem: T) => {
      const formTranslations: Translation[] = getValues("translations");
      updatedItem.translations.forEach((updatedTranslation) => {
        const formIndex = formTranslations.findIndex(
          (t) => t.uuid === updatedTranslation.uuid,
        );
        if (formIndex >= 0) {
          setValue(
            `translations.${formIndex}.picture`,
            updatedTranslation.picture,
          );
        }
      });
    },
    [getValues, setValue],
  );

  const { handleTagDelete, handleTagUpdate, tagState } = usePictureTags({
    itemData: item,
    itemType,
    onItemUpdate: handleItemUpdate,
  });

  const [loadings, setLoadings] = useState<Loadings>({});

  const handleDrop = ({
    file,
    translationId,
    pictureFormat,
  }: {
    file: File;
    translationId: string;
    pictureFormat: PictureKeys;
  }): void => {
    onUpload({
      file,
      translationId,
      pictureFormat,
    });
  };

  useEffect(() => {
    const { pictureFormat, translationId } = uploadPictureState;
    if (pictureFormat && translationId) {
      setLoadings((prev) => ({
        ...prev,
        [translationId]: {
          ...prev[translationId],
          [pictureFormat]: uploadPictureState.isLoading,
        },
      }));
    }
  }, [uploadPictureState]);

  const watchedTranslation = useWatch({
    control,
    name: `translations.${activeLanguageIndex ?? 0}`,
  }) as Translation | undefined;

  const lastValidTranslationRef = useRef(watchedTranslation);

  if (watchedTranslation) {
    lastValidTranslationRef.current = watchedTranslation;
  }

  const activeTranslation =
    watchedTranslation ?? lastValidTranslationRef.current;

  if (!activeTranslation) return null;

  return (
    <div className="flex gap-[0.625rem] overflow-auto p-[0.3125rem]">
      <div className="flex flex-wrap gap-[0.625rem]">
        {imageRatios.map((imageRatio) => (
          <PictureRatio
            key={imageRatio.id}
            handleDrop={handleDrop}
            imageRatio={imageRatio}
            isDisabled={isDisabled}
            isLoading={loadings?.[activeTranslation.uuid]?.[imageRatio.id]}
            onTagDelete={handleTagDelete}
            onTagUpdate={handleTagUpdate}
            tagState={tagState}
            translation={activeTranslation}
          />
        ))}
      </div>
    </div>
  );
}
